/**
 * ComparisonView.jsx — pure content component
 *
 * Receives pid + viewIds from Comparison (parent).
 * Each section gated by VIEW_IDS:
 *
 *   TOP_BOTTOM_PERFORMANCE    = 15  → Performance Matrix (top/bottom tables + criteria)
 *   TOP_BOTTOM_PERFORMANCE_HD = 16  → HaqdarshakLeaderboard (BR / DS tabs)
 *   HD_PERFORMANCE_VIEW       = 17  → HDPerformanceDashboard
 */

import React, { useState, useEffect } from 'react';
import HaqdarshakLeaderboard   from '../../KPI/Comparison/HDLeaderboard';
import HaqdarshakLeaderboardDS from '../../KPI/Comparison/HDLeaderboardDS';
import HDPerformanceDashboard  from '../../KPI/Comparison/HDPerformance';
import { VIEW_IDS }            from '../../constants/ViewIds';
import HDReport from './HDReport';

const API = 'https://api-project360-insights.haqdarshak.com';

const ComparisonView = ({ pid, viewIds = [] }) => {
  const canView = (id) => viewIds.includes(id);

  // ── shared criteria state (used by TOP_BOTTOM_PERFORMANCE) ───────────────
  const criteriaOptions = [
    { value: 'CitizenDistrict', label: 'District'     },
    { value: 'State',           label: 'State'        },
    { value: 'target_group',    label: 'Target Group' },
    { value: 'SchemeName',      label: 'Scheme Name'  },
  ];

  const limitOptions = [5, 10, 20, 50, 100, 1000];

  const [selectedCriteria, setSelectedCriteria] = useState('CitizenDistrict');
  const [selectedState,    setSelectedState]    = useState('');
  const [stateOptions,     setStateOptions]     = useState([]);
  
  // New state for limits
  const [topLimit,    setTopLimit]    = useState(5);
  const [bottomLimit, setBottomLimit] = useState(5);

  const [fullLeaderboard, setFullLeaderboard] = useState([]); // Store all data
  const [isLoading,        setIsLoading]        = useState(false);
  
  // Derived data - top and bottom based on limits
  const topPerforming = fullLeaderboard.slice(0, topLimit);
  const bottomPerforming = fullLeaderboard.slice(-bottomLimit).reverse();
  const totalRecords = fullLeaderboard.length;

  // ── leaderboard tab (TOP_BOTTOM_PERFORMANCE_HD) ───────────────────────────
  const [activeTab, setActiveTab] = useState('BR');

  // fetch state options for district filter
  useEffect(() => {
    if (!pid) return;
    fetch(`${API}/kpi/scheme-diversity/unique-state/?pid=${encodeURIComponent(pid)}`)
      .then(r => r.json())
      .then(d => setStateOptions(d.states || []))
      .catch(console.error);
  }, [pid]);

  // clear state filter when criteria changes away from district
  useEffect(() => {
    if (selectedCriteria !== 'CitizenDistrict') setSelectedState('');
  }, [selectedCriteria]);

  // fetch ALL performance data whenever pid / criteria / state changes
  useEffect(() => {
    if (!pid || !canView(VIEW_IDS.TOP_BOTTOM_PERFORMANCE)) return;
    const fetchPerf = async () => {
      setIsLoading(true);
      try {
        let url = `${API}/kpi/comparison/performance/?pid=${encodeURIComponent(pid)}&criteria=${encodeURIComponent(selectedCriteria)}`;
        if (selectedCriteria === 'CitizenDistrict' && selectedState)
          url += `&state=${encodeURIComponent(selectedState)}`;
        const res  = await fetch(url);
        const data = await res.json();
        setFullLeaderboard(data.leaderboard || []);
      } catch (err) {
        console.error('[ComparisonView] performance fetch error:', err);
        setFullLeaderboard([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPerf();
  }, [pid, selectedCriteria, selectedState, viewIds]);

  const handleRedirect = () =>
    window.open('https://usersreport-insights.haqdarshak.com/', '_blank');

  // guard — nothing to show
  const hasAny = canView(VIEW_IDS.TOP_BOTTOM_PERFORMANCE) ||
                 canView(VIEW_IDS.TOP_BOTTOM_PERFORMANCE_HD) ||
                 canView(VIEW_IDS.HD_PERFORMANCE_VIEW);

  if (!hasAny) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-400">
        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M12 15v2m0 0v2m0-2h2m-2 0H10M9 7h1m-1 4h1m4-4h1m-1 4h1M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <p className="text-lg font-medium">No views configured for this project.</p>
      </div>
    );
  }

  return (
    <div className="w-[90vw] ml-[3vw]">

      {/* ── TOP_BOTTOM_PERFORMANCE (id 15) — Performance Matrix ──────── */}
      {canView(VIEW_IDS.TOP_BOTTOM_PERFORMANCE) && (
        <div className="border-2 border-gray-300 shadow-md rounded-md overflow-hidden">
          
          {/* Header */}
          <div className="bg-gray-50 px-4 md:px-6 py-4 border-b">
            <h2 className="text-lg md:text-xl font-semibold text-pink-800">
              Performance Matrix
            </h2>
            <p className="text-sm text-pink-800 mt-1">
              Compare top and bottom performing entities
            </p>
          </div>

          {/* Filter Section - Similar to DiversityInSchemes */}
          <div className="bg-white p-4 md:p-6 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              
              {/* Criteria Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Criteria
                </label>
                <select
                  value={selectedCriteria}
                  onChange={(e) => setSelectedCriteria(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                >
                  {criteriaOptions.map((item, idx) => (
                    <option key={idx} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </div>

              {/* State Filter (conditional) */}
              {selectedCriteria === 'CitizenDistrict' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by State (Optional)
                  </label>
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                  >
                    <option value="">All States</option>
                    {stateOptions.map((st, i) => (
                      <option key={i} value={st}>{st}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Top Limit Selection - Like DiversityInSchemes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Top Entities
                </label>
                <select
                  value={topLimit}
                  onChange={(e) => setTopLimit(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                >
                  {limitOptions.map(limit => (
                    <option key={`top-${limit}`} value={limit}>{limit}</option>
                  ))}
                </select>
              </div>

              {/* Bottom Limit Selection - Like DiversityInSchemes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bottom Entities
                </label>
                <select
                  value={bottomLimit}
                  onChange={(e) => setBottomLimit(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                >
                  {limitOptions.map(limit => (
                    <option key={`bottom-${limit}`} value={limit}>{limit}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Total records info - Like DiversityInSchemes */}
            {totalRecords > 0 && (
              <div className="mt-3 text-sm text-gray-500">
                Total unique {selectedCriteria.toLowerCase()} records: <span className="font-semibold">{totalRecords}</span>
              </div>
            )}
          </div>

          {/* Performance Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4 md:p-6 bg-gray-50">
            
            {/* Top Performing Table */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-4 py-3">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <h3 className="text-white font-semibold">
                    Top {topLimit} Performing {selectedCriteria === 'CitizenDistrict' ? 'Districts' : 
                               selectedCriteria === 'State' ? 'States' :
                               selectedCriteria === 'target_group' ? 'Target Groups' : 'Schemes'}
                  </h3>
                </div>
              </div>
              
              <div className="p-4">
                {isLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-3 px-3 text-sm font-semibold text-gray-600">Rank</th>
                          <th className="text-left py-3 px-3 text-sm font-semibold text-gray-600">Name</th>
                          <th className="text-right py-3 px-3 text-sm font-semibold text-gray-600">BR Cases</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topPerforming.length > 0 ? (
                          topPerforming.map((item) => (
                            <tr key={item.rank} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="py-3 px-3">
                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                                  item.rank === 1 ? 'bg-yellow-500 text-white' :
                                  item.rank === 2 ? 'bg-gray-400 text-white' :
                                  item.rank === 3 ? 'bg-orange-600 text-white' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {item.rank}
                                </span>
                              </td>
                              <td className="py-3 px-3 font-medium text-gray-800 break-words max-w-xs">{item.name}</td>
                              <td className="py-3 px-3 text-right font-semibold text-green-600">{item.br_value.toLocaleString()}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center py-8 text-gray-400">No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Performing Table */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-4 py-3">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                  <h3 className="text-white font-semibold">
                    Bottom {bottomLimit} Performing {selectedCriteria === 'CitizenDistrict' ? 'Districts' : 
                                selectedCriteria === 'State' ? 'States' :
                                selectedCriteria === 'target_group' ? 'Target Groups' : 'Schemes'}
                  </h3>
                </div>
              </div>
              
              <div className="p-4">
                {isLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-3 px-3 text-sm font-semibold text-gray-600">Rank</th>
                          <th className="text-left py-3 px-3 text-sm font-semibold text-gray-600">Name</th>
                          <th className="text-right py-3 px-3 text-sm font-semibold text-gray-600">BR Cases</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bottomPerforming.length > 0 ? (
                          bottomPerforming.map((item) => (
                            <tr key={item.rank} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="py-3 px-3">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm bg-gray-200 text-gray-700">
                                  {item.rank}
                                </span>
                              </td>
                              <td className="py-3 px-3 font-medium text-gray-800 break-words max-w-xs">{item.name}</td>
                              <td className="py-3 px-3 text-right font-semibold text-red-600">{item.br_value.toLocaleString()}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center py-8 text-gray-400">No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Redirect Button */}
          <div className="flex justify-center p-6 bg-gray-50 border-t border-gray-200">
            <button
              onClick={handleRedirect}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium px-6 py-2 rounded-lg shadow-md transition-all duration-200"
            >
              Click here to see Achievement Dashboard
            </button>
          </div>
        </div>
      )}

      {/* ── TOP_BOTTOM_PERFORMANCE_HD (id 16) — Haqdarshak Leaderboard ─────── */}
      {canView(VIEW_IDS.TOP_BOTTOM_PERFORMANCE_HD) && (
        <div className="mt-8">
          <div className="flex space-x-2 mb-4 ml-[40vw]">
            <button
              onClick={() => setActiveTab('BR')}
              className={`px-4 py-2 rounded-lg font-medium shadow-md transition ${activeTab === 'BR' ? 'bg-pink-200 text-pink-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >BR</button>
            <button
              onClick={() => setActiveTab('DS')}
              className={`px-4 py-2 rounded-lg font-medium shadow-md transition ${activeTab === 'DS' ? 'bg-pink-200 text-pink-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >DS</button>
          </div>
          {activeTab === 'BR'
            ? <HaqdarshakLeaderboard   pid={pid} />
            : <HaqdarshakLeaderboardDS pid={pid} />}
        </div>
      )}

      {/* ── HD_PERFORMANCE_VIEW (id 17) — HD Performance Dashboard ──────────── */}
      {canView(VIEW_IDS.HD_PERFORMANCE_VIEW) && (
        <div>
          <HDPerformanceDashboard pid={pid} />
        </div>
      )}

      <HDReport/>

    </div>
  );
};

export default ComparisonView;